<template lang="pug">
modal#show-envelope-info-modal(
  :cancel-action='cancel'
  :close-button-label='$t("button.cancel")'
  :disable-process-button='!form.checkTermsAndConditions && !hideFormAcceptTerms'
  :loading='loading'
  :process-button-label='getProcessButtonLabel()'
  :removeCloseButton='true'
  :show-cancel-button='showWarningLogoutForAction'
  :title='$t("modal.showEnvelopeInfoModal.title")'
  @closed='onCloseModal'
  @process='processAction'
  ref='modal'
  size='sm'
)
  .inner-content.font-size-small(v-if='envelope')
    p {{ $t('modal.showEnvelopeInfoModal.sentBy') }}:
      |
      | {{ envelope.sender.name }}
    p {{ dateTitle }}:
      |
      | {{ formatDate(envelope.expire_at) }}
    signatures-list.mt-4(
      :envelope='envelope'
      :hidden='showWarningLogoutForAction'
    )
    form.form.mt-3(v-if='!hideFormAcceptTerms')
      label(for='terms-checkbox')
        input#terms-checkbox.form-check-input(
          type='checkbox'
          v-model='form.checkTermsAndConditions'
        )
        | &nbsp; {{ $t('modal.showEnvelopeInfoModal.termsAndConditions') }}
        a(:href='`${baseUrlAuth}/terms`' target='_blank') &nbsp; {{ $t('modal.showEnvelopeInfoModal.termsOfUse') }}
        | &nbsp; {{ $t('modal.showEnvelopeInfoModal.and') }}
        a(:href='`${baseUrlAuth}/privacy`' target='_blank') &nbsp; {{ $t('modal.showEnvelopeInfoModal.privacyPolicy') }}
  template(v-if='showWarningLogoutForAction')
    .show-warning-logout.font-size-small.mt-4
      p {{ $t('modal.showEnvelopeInfoModal.logoutWarning', { toAction: getRecipientActionText }) }}
    addressee-list.mt-3(:addressee='addressee')
</template>

<script>
import Modal from '@/ui/molecules/Modal.vue';
import EnvelopeState from '@/enums/EnvelopeState';
import IconStatus from '@/ui/atoms/IconStatus.vue';
import SignaturesList from '@/modules/business/components/molecules/SignaturesList.vue';
import AddresseeList from '@/modules/business/components/molecules/AddresseeList.vue';
import AppConfig from '@/config';
import AccountHttp from '@/services/AccountHttp';
import LocalStorage from '@/services/LocalStorage';
import RecipientType from '@/enums/RecipientType';

export default {
  name: 'showEnvelopeInfoModal',
  components: {
    Modal,
    IconStatus,
    SignaturesList,
    AddresseeList,
  },
  props: {
    envelope: {
      type: Object,
      required: true,
    },
    hideFormAcceptTerms: {
      type: Boolean,
      default: false,
    },
    recipientType: {
      type: String,
      required: true,
    },
    isPublic: {
      type: Boolean,
      required: false,
      default: false,
    },
    canViewEnvelope: {
      type: Boolean,
      required: false,
      default: false,
    },
    addressee: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      loading: false,
      form: {
        checkTermsAndConditions: false,
      },
      route: {},
    };
  },
  mounted() {
    this.route = this.$route;
  },
  methods: {
    getProcessButtonLabel() {
      if (this.canViewEnvelope || !this.hasLoggedUser || this.isPublic) {
        return this.$t('modal.showEnvelopeInfoModal.processButtonLabel');
      }
      return this.$t('modal.showEnvelopeInfoModal.proccessButtonLogoutAndSign');
    },
    async processAction() {
      if (!this.canViewEnvelope && this.hasLoggedUser && !this.isPublic) {
        this.$loading = true;
        const res = await AccountHttp.logout();
        if (res.status === 204) {
          this.$root.user.name = '';
          this.$root.user.avatar = '';
          LocalStorage.set('organization', false);
          const publicEnvelopeRoute = {
            name: 'public.envelopes.viewer',
            params: this.route.params,
            query: this.route.query,
            hash: this.route.hash,
            replace: true,
          };
          if (this.$route.name !== publicEnvelopeRoute.name) {
            await this.$router.replace(publicEnvelopeRoute);
          }
        }
        this.$loading = false;
      }
      this.acceptTerms();
    },
    acceptTerms() {
      this.$emit('process');
      if (this.$refs.modal) {
        this.$refs.modal.close();
      }
    },
    onCloseModal() {
      this.$refs.modal.close();
      this.$emit('closed');
    },
    cancel() {
      // go to home page
      const homePageRoute = {
        name: 'business.dashboard',
        replace: true,
      };
      if (this.$refs.modal) {
        this.$refs.modal.close();
      }
      if (this.$route.name !== homePageRoute.name) {
        this.$router.replace(homePageRoute);
      }
    },
    formatDate(date) {
      if (date) {
        return new Date(date).toLocaleString(this.$i18n.locale, {
          timeZone: 'UTC',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
      }
      return this.$t('modal.showEnvelopeInfoModal.noDeadline');
    },
    getAddresseeLabel(addresseeType) {
      const typeValue = RecipientType.values.filter(
        (type) => type.value === addresseeType
      );

      let label = '';
      switch (typeValue[0].value) {
        case 'approver':
          label = 'toApprove';
          break;
        case 'signer':
          label = 'toSign';
          break;
        case 'viewer':
          label = 'toView';
          break;
        case 'carbon-copy':
          label = 'toView';
          break;
        default:
          label = typeValue[0].value;
      }

      return this.$t(`modal.envelope.${label}`);
    },
  },
  computed: {
    showWarningLogoutForAction() {
      if (!this.canViewEnvelope && this.hasLoggedUser && !this.isPublic) {
        return true;
      }
      return false;
    },
    envelopeStateText() {
      return EnvelopeState.getText(this.envelope.state);
    },
    baseUrlAuth() {
      return AppConfig.authorizationServer;
    },
    hasLoggedUser() {
      return this.$root.user?.name !== '';
    },
    dateTitle() {
      if (this.envelope.state === 'expired')
        return this.$t('modal.showEnvelopeInfoModal.expiredOn');
      if (this.recipientType === 'approver')
        return this.$t('modal.showEnvelopeInfoModal.approvalDeadline');
      if (this.recipientType === 'signer')
        return this.$t('modal.showEnvelopeInfoModal.signingDeadline');
      return this.$t('modal.showEnvelopeInfoModal.completionDeadline');
    },
    getRecipientActionText() {
      return this.getAddresseeLabel(this.addressee.type);
    },
  },
};
</script>
<style lang="stylus" scoped>
.inner-content
  a
    color: #01AFEC
  p
    margin-bottom: 0
.show-warning-logout
  border: 1px solid #f5c6cb
  border-radius: 5px
  font-weight: 400
  padding: 0.5rem
  background-color: #f8d7da
  color: #721C24
  text-align: center
  p
    margin-bottom: 0
:deep(.modal-footer button.btn-secondary)
  border-color: #d64242 !important;
  background-color: #d64242 !important;
  color: #fff !important;
:deep(.modal-footer)
  justify-content: center !important;
</style>
